import React from 'react';

const BatteryGauge = ({
  percentage = 50,
  width = 120,
  height = 200,
  showPercentage = true,
}) => {
  const fillLevel = Math.max(0, Math.min(100, percentage));

  const getBatteryColor = percent => {
    if (percent > 60) return '#22c55e';
    if (percent > 30) return '#ffc107';
    if (percent > 15) return '#fd7e14';
    return '#dc3545';
  };

  const batteryColor = getBatteryColor(fillLevel);
  const bodyWidth = width * 0.75;
  const bodyHeight = height - 20;
  const terminalWidth = bodyWidth * 0.35;
  const terminalHeight = 12;

  return (
    <div
      style={{
        width: width,
        height: height,
        position: 'relative',
        display: 'inline-block',
        margin: '0 auto',
        filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.15))',
        animation: 'pulse 2s ease-in-out infinite',
      }}
    >
      <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient
            id="batteryGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop
              offset="0%"
              style={{ stopColor: batteryColor, stopOpacity: 1 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: batteryColor, stopOpacity: 0.7 }}
            />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect
          x={(width - terminalWidth) / 2}
          y="0"
          width={terminalWidth}
          height={terminalHeight}
          fill="#374151"
          rx="3"
          ry="3"
        />

        <rect
          x={(width - bodyWidth) / 2}
          y={terminalHeight}
          width={bodyWidth}
          height={bodyHeight}
          fill="#ffffff"
          stroke="#374151"
          strokeWidth="3"
          rx="8"
          ry="8"
        />

        <rect
          x={(width - bodyWidth) / 2 + 3}
          y={
            terminalHeight +
            bodyHeight -
            3 -
            (bodyHeight - 6) * (fillLevel / 100)
          }
          width={bodyWidth - 6}
          height={(bodyHeight - 6) * (fillLevel / 100)}
          fill="url(#batteryGradient)"
          rx="5"
          ry="5"
          filter="url(#glow)"
          style={{
            transition: 'all 0.5s ease-in-out',
          }}
        />

        {fillLevel > 0 && fillLevel < 100 && (
          <rect
            x={(width - bodyWidth) / 2 + 3}
            y={
              terminalHeight +
              bodyHeight -
              3 -
              (bodyHeight - 6) * (fillLevel / 100)
            }
            width={bodyWidth - 6}
            height="3"
            fill="#ffffff"
            opacity="0.6"
            rx="2"
            style={{
              animation: 'chargeWave 1.5s ease-in-out infinite',
            }}
          />
        )}
      </svg>

      {showPercentage && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '26px',
            fontWeight: '900',
            color: fillLevel > 30 ? '#ffffff' : '#374151',
            textShadow: fillLevel > 30 ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none',
            userSelect: 'none',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            letterSpacing: '-0.5px',
          }}
        >
          {Math.round(fillLevel)}%
        </div>
      )}
    </div>
  );
};

const calcularPorcentagemPagamento = (totalDivida, totalPago) => {
  return totalDivida > 0 ? (totalPago / totalDivida) * 100 : 0;
};

const BatteryDemo = ({ totalDivida = 0, totalPago = 0 }) => {
  const porcentagemPagamento = calcularPorcentagemPagamento(
    totalDivida,
    totalPago
  );

  const getStatusText = percentage => {
    if (percentage > 90) return 'Excelente';
    if (percentage > 80) return 'Ótimo';
    if (percentage > 60) return 'Bom';
    if (percentage > 40) return 'Regular';
    if (percentage > 20) return 'Ruim';
    return 'Crítico';
  };

  const getStatusColor = percentage => {
    if (percentage > 90) return '#22c55e';
    if (percentage > 80) return '#10b981';
    if (percentage > 60) return '#3b82f6';
    if (percentage > 40) return '#ffc107';
    if (percentage > 20) return '#fd7e14';
    return '#dc3545';
  };

  const getStatusEmoji = percentage => {
    if (percentage > 90) return '🎯';
    if (percentage > 80) return '✨';
    if (percentage > 60) return '📈';
    if (percentage > 40) return '⚡';
    if (percentage > 20) return '⚠️';
    return '⚠️';
  };

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const statusText = getStatusText(porcentagemPagamento);
  const statusColor = getStatusColor(porcentagemPagamento);
  const statusEmoji = getStatusEmoji(porcentagemPagamento);
  const restante = totalDivida - totalPago;

  return (
    <div
      style={{
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        margin: '0 auto',
        height: '100%',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '25px',
          paddingTop: '0.5rem',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        }}
      >
        <div style={{ marginBottom: '35px', textAlign: 'center' }}>
          <h2
            style={{
              fontSize: '26px',
              fontWeight: '700',
              background:
                'linear-gradient(135deg, #66ea8eff 0%, #72ff5fff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '10px',
              margin: '0 0 10px 0',
              letterSpacing: '-0.5px',
            }}
          >
            Status dos Pagamentos
          </h2>
          <p
            style={{
              fontSize: '16px',
              color: '#64748b',
              margin: '0',
              fontWeight: '500',
            }}
          >
            Acompanhe o progresso dos acordos realizados em tempo real
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: '50px',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              flex: '0 0 auto',
              textAlign: 'center',
              minWidth: '180px',
              borderRadius: '20px',
              padding: '30px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BatteryGauge
              percentage={porcentagemPagamento}
              width={100}
              height={160}
              showPercentage={true}
            />
            <div
              style={{
                fontSize: '20px',
                fontWeight: '700',
                color: statusColor,
                marginTop: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>{statusEmoji}</span>
              <span>{statusText}</span>
            </div>
          </div>

          <div
            style={{
              flex: '1',
              minWidth: '320px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gap: '15px',
              }}
            >
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  padding: '11px',
                  borderRadius: '16px',
                  color: 'white',
                  boxShadow: '0 10px 25px rgba(139, 92, 246, 0.25)',
                  gridColumn: 'span 2',
                  transform: 'translateY(0)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow =
                    '0 15px 35px rgba(139, 92, 246, 0.35)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 10px 25px rgba(139, 92, 246, 0.25)';
                }}
              >
                <div
                  style={{
                    fontSize: '14px',
                    opacity: '0.9',
                    marginBottom: '8px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span>📊</span> TOTAL EM ATRASO NEGOCIADO
                </div>
                <div
                  style={{
                    fontSize: '22px',
                    fontWeight: '700',
                  }}
                >
                  {formatCurrency(totalDivida)}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    flex: '1',
                    minWidth: '140px',
                    backgroundColor: '#ffffff',
                    padding: '14px 16px',
                    border: '2px solid #10b981',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#f0fdf4';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px rgba(16, 185, 129, 0.15)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>✓</span>
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#10b981',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Total Pago
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#10b981',
                    }}
                  >
                    {formatCurrency(totalPago)}
                  </div>
                </div>
              </div>
              <div
                style={{
                  flex: '1',
                  minWidth: '140px',
                  backgroundColor: '#ffffff',
                  padding: '14px 16px',
                  border: '2px solid #ef4444',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#fef2f2';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(239, 68, 68, 0.15)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '6px',
                  }}
                >
                  <span style={{ fontSize: '14px' }}>⏳</span>
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#ef4444',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Restante
                  </span>
                </div>
                <div
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#ef4444',
                  }}
                >
                  {formatCurrency(restante)}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: '20px',
                padding: '15px',
                background: 'rgba(102, 126, 234, 0.05)',
                borderRadius: '12px',
                borderLeft: '4px solid',
                borderImage:
                  'linear-gradient(135deg, #667eea 0%, #764ba2 100%) 1',
              }}
            >
              <p
                style={{
                  fontSize: '14px',
                  color: '#64748b',
                  margin: '0',
                  lineHeight: '1.6',
                }}
              >
                {porcentagemPagamento >= 60
                  ? '🎉 Parabéns! Você está no caminho certo para quitar seus acordos.'
                  : porcentagemPagamento >= 30
                  ? '💪 Continue assim! Cada pagamento te aproxima do seu objetivo.'
                  : '📈 Vamos acelerar o ritmo dos pagamentos para alcançar suas metas.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
        }

        @keyframes chargeWave {
          0% {
            transform: translateY(0);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-10px);
            opacity: 0.3;
          }
          100% {
            transform: translateY(-20px);
            opacity: 0;
          }
        }

        @media (max-width: 1353px) {
          div[style*='flex-wrap: wrap'] {
            flex-direction: column;
            align-items: stretch;
            gap: 25px;
          }

          div[style*="minWidth: '180px'"] {
            width: 100%;
          }

          div[style*="minWidth: '320px'"] {
            width: 100%;
          }

          div[style*="gridColumn: 'span 2'"] {
            grid-column: span 1 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BatteryDemo;
